import React from 'react';
import { Link, Redirect, Router } from '@reach/router';
import { compose, lifecycle } from 'recompose';
import {
  Card,
  CardCol,
  CardRow,
  ErrorMessage,
  LoadingMessage,
  ErrorNotFound,
} from '@kineticdata/bundle-common';
import { PageTitle } from '../shared/PageTitle';
import { actions as formActions } from '../../redux/modules/settingsForms';
import { FormSettings } from './forms/FormSettings';
import { FormSubmissions } from './forms/FormSubmissions';
import { FormActivity } from './forms/FormActivity';
import { I18n } from '@kineticdata/react';
import { connect } from '../../redux/store';

import {
  CatalogManagementWrapper,
  CatalogManagement,
} from './catalog/CatalogManagement';
import { Portfolio } from './catalog/Portfolio';
import { PortfolioRevision } from './catalog/PortfolioRevision';
import { Service } from './catalog/Service';
import { ServiceRevision } from './catalog/ServiceRevision';
import { Appendix } from './catalog/Appendix';
import { AppendixRevision } from './catalog/AppendixRevision';
import { Ownership } from './catalog/Ownership';

// Wrapper for components that require the form object
export const FormSettingsWrapper = compose(
  connect(
    state => ({
      kapp: state.app.kapp,
      form: state.settingsForms.form,
      error: state.settingsForms.error,
    }),
    { fetchFormRequest: formActions.fetchFormRequest },
  ),
  lifecycle({
    componentWillMount(prev, next) {
      this.props.fetchFormRequest({
        kappSlug: this.props.kapp.slug,
        formSlug: this.props.formSlug,
      });
    },
  }),
)(
  ({ form, error, kapp }) =>
    error || !form ? (
      <div className="page-container">
        <div className="page-panel">
          <PageTitle
            parts={[form && form.name, `Forms`]}
            settings
            breadcrumbs={[
              { label: 'Home', to: '/' },
              { label: `${kapp.name} Settings`, to: '../..' },
              { label: 'Forms', to: '..' },
            ]}
          />
          {error ? (
            <ErrorMessage message={error.message} />
          ) : (
            <LoadingMessage />
          )}
        </div>
      </div>
    ) : (
      <Router>
        <FormSettings form={form} path="settings" />
        <FormActivity form={form} path="submissions/:id" />
        <FormSubmissions form={form} default />
      </Router>
    ),
);

export const Settings = () => (
  <Router>
    <CatalogManagementWrapper path="catalog">
      <Redirect from="/" to="portfolios" noThrow />
      <Ownership exact path="ownership" />
      <CatalogManagement path=":type" />
      <Portfolio path="portfolios/:portfolioId" />
      <Portfolio path="portfolios/:portfolioId/:mode" />
      <PortfolioRevision path="portfolios/:portfolioId/revision/:revisionId" />
      <PortfolioRevision path="portfolios/:portfolioId/revision/:revisionId/:mode" />
      <Service path="services/:serviceId" />
      <Service path="services/:serviceId/:mode" />
      <ServiceRevision path="services/:serviceId/revision/:revisionId" />
      <ServiceRevision path="services/:serviceId/revision/:revisionId/:mode" />
      <Service path="portfolios/:portfolioId/services/:serviceId" />
      <Service path="portfolios/:portfolioId/services/:serviceId/:mode" />
      <ServiceRevision path="portfolios/:portfolioId/services/:serviceId/revision/:revisionId" />
      <ServiceRevision path="portfolios/:portfolioId/services/:serviceId/revision/:revisionId/:mode" />
      <Appendix path="appendices/:appendixId" />
      <Appendix path="appendices/:appendixId/:mode" />
      <AppendixRevision path="appendices/:appendixId/revision/:revisionId" />
      <AppendixRevision path="appendices/:appendixId/revision/:revisionId/:mode" />
      <Appendix path="services/:serviceId/appendices/:appendixId" />
      <Appendix path="services/:serviceId/appendices/:appendixId/:mode" />
      <AppendixRevision path="services/:serviceId/appendices/:appendixId/revision/:revisionId" />
      <AppendixRevision path="services/:serviceId/appendices/:appendixId/revision/:revisionId/:mode" />
      <Appendix path="services/:serviceId/appendices/:appendixId" />
      <Appendix path="services/:serviceId/appendices/:appendixId/:mode" />
      <AppendixRevision path="portfolios/:portfolioId/services/:serviceId/appendices/:appendixId/revision/:revisionId" />
      <AppendixRevision path="portfolios/:portfolioId/services/:serviceId/appendices/:appendixId/revision/:revisionId/:mode" />
    </CatalogManagementWrapper>
    <ErrorNotFound default />
  </Router>
);

const SettingsCard = ({ path, icon, name, description }) => (
  <Card
    to={path}
    color="light"
    components={{ Link }}
    bar={true}
    barColor="dark"
    barSize="xs"
  >
    <CardCol>
      <CardRow type="title">
        <span
          className={`fa fa-${(icon || 'cog').replace(
            /^fa-/i,
            '',
          )} fa-fw fa-rounded bg-dark`}
        />
        <span>
          <I18n>{name}</I18n>
        </span>
      </CardRow>
      <CardRow className="text-muted">{description}</CardRow>
    </CardCol>
  </Card>
);

const SettingsNavigationComponent = ({ kapp, isSpaceAdmin }) => (
  <div className="page-container">
    <div className="page-panel">
      <PageTitle
        settings
        breadcrumbs={[{ label: 'Home', to: '/' }]}
        title={`${kapp.name} Settings`}
      />

      <div className="cards cards--seconds">
        {isSpaceAdmin && (
          <SettingsCard
            name="General Settings"
            path="general"
            icon="fa-gear"
            description="View and Modify all Services Settings"
          />
        )}
        <SettingsCard
          name="Forms"
          path="forms"
          icon="fa-gear"
          description="View Forms and their Submissions."
        />
        {isSpaceAdmin && (
          <SettingsCard
            name="Categories"
            path="categories"
            icon="fa-gear"
            description="View and Modify Categories"
          />
        )}
      </div>
    </div>
  </div>
);

const mapStateToPropsNav = state => ({
  kapp: state.app.kapp,
  isSpaceAdmin: state.app.profile.spaceAdmin,
});

export const SettingsNavigation = compose(connect(mapStateToPropsNav))(
  SettingsNavigationComponent,
);
